import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { TablePagination, CircularProgress } from '@mui/material';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { sa, DataTable, PillButton } from '../superAdminUi';
import { formatLastLogin, SORT_FIELDS } from './userFields';
import { describeAccessLevel } from './accessLevel';
import UserActionsMenu from './UserActionsMenu';
import ActivityHistory from './ActivityHistory';
import { accountStatusDisplay, showsLifecycle } from './userLifecycle';

// The users list, rendered through the shared Super Admin DataTable (purple header + zebra
// rows). Server-side sorting/paging drive up through the callbacks; a header click the backend
// ignores simply re-fetches the same order (graceful, not broken). Per the approved design a
// group view adds a School Name column (resolved from the group's own schools); a single-school
// view has no such column (User Name / Last Login / Access Level / Action only).

// A purple-header sort control: label plus a direction glyph that reflects the active column.
const SortHeader = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  color: ${sa.color.white};
  font-family: ${sa.font.family};
  font-weight: ${sa.font.bold};
  font-size: inherit;

  svg {
    font-size: 16px;
    opacity: ${(p) => (p.$active ? 1 : 0.55)};
  }
`;

const NameCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;
const UserName = styled.span`
  font-weight: ${sa.font.semibold};
  color: ${sa.color.text};
  overflow: hidden;
  text-overflow: ellipsis;
`;
// The shared table clips cells to one line; a role summary is only useful read in full, so it wraps.
const AccessLevelText = styled.span`
  display: block;
  white-space: normal;
  overflow-wrap: anywhere;
`;
const UserEmail = styled.span`
  font-size: 12px;
  color: ${sa.color.textGray};
  overflow: hidden;
  text-overflow: ellipsis;
`;
const InactiveChip = styled.span`
  align-self: flex-start;
  margin-top: 4px;
  padding: 2px 8px;
  border-radius: ${sa.radius.sm};
  background: ${sa.color.mist};
  color: ${sa.color.textGray};
  font-size: 11px;
  font-weight: ${sa.font.bold};
  line-height: 1.4;
`;

// Account-lifecycle status pill, shown in the name cell only once the grid is filtered
// off the default Active view. Colours come from userLifecycle (semantic, not sa tokens).
const StatusPill = styled.span`
  align-self: flex-start;
  margin-top: 4px;
  padding: 2px 9px;
  border-radius: 20px;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$fg};
  font-size: 11px;
  font-weight: ${sa.font.semibold};
  line-height: 1.5;
`;

const ActionCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StateRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: ${(p) => (p.$error ? sa.color.pink : sa.color.textGray)};
  font-family: ${sa.font.family};
  font-size: 13px;
`;

const ExpandPanel = styled.div`
  border-left: 3px solid ${sa.color.purple};
  background: #fafbfc;
  padding: 18px 24px;
`;

const PaginationWrap = styled.div`
  .MuiTablePagination-root {
    font-family: ${sa.font.family};
    color: ${sa.color.textGray};
  }
`;

function UsersTable({
  scope,
  users,
  total,
  page,
  pageSize,
  sortBy,
  sortDescending,
  loading,
  error,
  onSortChange,
  onPageChange,
  onPageSizeChange,
  onAction,
  schoolNamesById = {},
  statusFilter,
}) {
  const [expandedId, setExpandedId] = useState(null);
  const isGroup = scope === 'group';
  // The lifecycle affordances (status pill + status-aware action label) only show once the
  // grid is filtered off the default Active view — the default stays exactly as before.
  const showStatus = showsLifecycle(statusFilter);

  // Resolve a user's own scoped school ids to names via the group's map. A user scoped to the
  // whole group (no specific schools) reads as "All schools"; an unmapped id falls back to itself.
  const resolveSchools = (user) => {
    const ids = user.schoolIds || [];
    if (ids.length) return ids.map((id) => schoolNamesById[id] || `School ${id}`).join(', ');
    if ((user.groupIds || []).length) return 'All schools';
    return '—';
  };

  const toggleHistory = (id) => setExpandedId((current) => (current === id ? null : id));

  const sortHeader = (field, label) => {
    const active = sortBy === SORT_FIELDS[field];
    const Glyph = active ? (sortDescending ? ArrowDownwardIcon : ArrowUpwardIcon) : SwapVertIcon;
    return (
      <SortHeader type='button' $active={active} onClick={() => onSortChange(field)}>
        {label}
        <Glyph />
      </SortHeader>
    );
  };

  const nameColumn = {
    key: 'name',
    header: sortHeader('name', 'User Name'),
    width: isGroup ? '30%' : '45%',
    render: (user) => {
      const pill = showStatus ? accountStatusDisplay(user.status, user.inviteExpired) : null;
      return (
        <NameCell>
          <UserName>{user.name || '—'}</UserName>
          <UserEmail>{user.email}</UserEmail>
          {pill ? (
            <StatusPill $bg={pill.bg} $fg={pill.fg}>{pill.label}</StatusPill>
          ) : (
            user.isInactive && <InactiveChip>Inactive</InactiveChip>
          )}
        </NameCell>
      );
    },
  };

  const lastLoginColumn = {
    key: 'lastLogin',
    header: sortHeader('lastLogin', 'Last Login'),
    render: (user) => formatLastLogin(user.lastLogin),
  };

  const accessLevelColumn = {
    key: 'accessLevel',
    label: 'Access Level',
    render: (user) => {
      const accessLevel = describeAccessLevel(user);
      return <AccessLevelText title={accessLevel}>{accessLevel}</AccessLevelText>;
    },
  };

  const schoolColumn = {
    key: 'school',
    label: 'School Name',
    render: (user) => resolveSchools(user),
  };

  const actionColumn = {
    key: 'action',
    label: 'Action',
    width: '260px',
    render: (user) => (
      <ActionCell>
        <PillButton type='button' $variant='danger' onClick={() => toggleHistory(user.id)}>
          {expandedId === user.id ? 'Hide History' : 'Reveal History'}
        </PillButton>
        <UserActionsMenu user={user} onSelect={onAction} showLifecycle={showStatus} />
      </ActionCell>
    ),
  };

  const columns = isGroup
    ? [nameColumn, lastLoginColumn, accessLevelColumn, schoolColumn, actionColumn]
    : [nameColumn, lastLoginColumn, accessLevelColumn, actionColumn];

  const emptyNode = loading ? (
    <StateRow>
      <CircularProgress size={18} sx={{ color: sa.color.teal }} />
      Loading users…
    </StateRow>
  ) : error ? (
    <StateRow $error>{error}</StateRow>
  ) : (
    'No users found.'
  );

  const rows = loading || error ? [] : users;

  return (
    <div>
      <DataTable
        columns={columns}
        rows={rows}
        getRowKey={(user) => user.id}
        empty={emptyNode}
        isRowExpanded={(user) => user.id === expandedId}
        renderExpanded={(user) => (
          <ExpandPanel>
            <ActivityHistory userId={user.id} />
          </ExpandPanel>
        )}
      />

      {total !== undefined && (
        <PaginationWrap>
          <TablePagination
            component='div'
            count={total}
            page={page}
            rowsPerPage={pageSize}
            onPageChange={(_event, next) => onPageChange(next)}
            onRowsPerPageChange={(event) => onPageSizeChange(parseInt(event.target.value, 10))}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </PaginationWrap>
      )}
    </div>
  );
}

UsersTable.propTypes = {
  scope: PropTypes.oneOf(['school', 'group']).isRequired,
  users: PropTypes.arrayOf(PropTypes.object).isRequired,
  total: PropTypes.number,
  page: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  sortBy: PropTypes.string,
  sortDescending: PropTypes.bool,
  loading: PropTypes.bool,
  error: PropTypes.string,
  onSortChange: PropTypes.func.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onPageSizeChange: PropTypes.func.isRequired,
  onAction: PropTypes.func.isRequired,
  schoolNamesById: PropTypes.object,
  statusFilter: PropTypes.string,
};

export default UsersTable;
